import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { FULLSCREEN_ASIDE_CSS_WIDTH } from "../vendor/liveTapRenderer";
// .tapestry-fs-btn: the button's whole chrome, shared with the classic canvas.
import "../vendor/liveTapRenderer/styles/tapestryAnimations.css";

/**
 * Fullscreen for the v2 live views (TT-1150): the same control the classic
 * canvas carries (TapestryCanvas' `.tapestry-fs-btn`), as a host wrapper. A
 * CSS takeover, not the Fullscreen API, like classic: a fixed backdrop, the
 * stage fixed over it, Esc or the button to leave. The v2 canvases already
 * size to the viewport through `--stage-cap` (default: the page's height
 * cap), so fullscreen just raises that cap to the whole viewport.
 *
 * `aside` is host chrome for a gutter at the right edge while fullscreen —
 * the transcript panel, as classic shows it (TT-1174 note 3). The gutter's
 * width is the renderer's one rule (as CSS here; classic computes the
 * number because its scale needs it), so the panel sits in the same column
 * whichever live system is up; the canvas keeps the rest. `onChange`
 * reports the fullscreen state, so the host can stand its inline placement
 * of that chrome down while the gutter shows it.
 */

// The stage is a row: the canvas column, then the gutter. The column is
// centred vertically: the canvases size themselves from their container's
// width (100%, an aspect ratio, a max-width from the height cap), and a
// column's cross axis stretches the content to the column's width — in a
// row it shrank to a percentage of nothing: a black screen (TT-1168 note 7).
const STAGE_ON = { position: "fixed", inset: 0, zIndex: 9999, display: "flex", "--stage-cap": "100vh" };
const COLUMN_ON = { position: "relative", flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" };
const COLUMN_OFF = { position: "relative" };
const GUTTER = { flex: "0 0 auto", width: FULLSCREEN_ASIDE_CSS_WIDTH, display: "flex" };
const BACKDROP = { position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.95)" };

export default function FullscreenStage({ aside = null, onChange, children }) {
  const [on, setOn] = useState(false);
  const toggle = useCallback((e) => {
    e.stopPropagation();
    setOn((v) => !v);
  }, []);
  useEffect(() => {
    if (!on) return undefined;
    const onKey = (e) => e.key === "Escape" && setOn(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on]);
  useEffect(() => {
    onChange?.(on);
  }, [on, onChange]);

  return (
    <>
      {on && <div style={BACKDROP} onClick={() => setOn(false)} />}
      <div style={on ? STAGE_ON : undefined}>
        <div style={on ? COLUMN_ON : COLUMN_OFF}>
          {children}
          <button
            type="button"
            data-export-ignore
            onClick={toggle}
            className="tapestry-fs-btn"
            title={on ? "Exit fullscreen (Esc)" : "Fullscreen"}
            aria-label={on ? "Exit fullscreen" : "Fullscreen"}
          >
            {on ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </button>
        </div>
        {on && aside && <div style={GUTTER}>{aside}</div>}
      </div>
    </>
  );
}

FullscreenStage.propTypes = {
  aside: PropTypes.node,
  onChange: PropTypes.func,
  children: PropTypes.node,
};
